import type { PropAttributeTable } from "building-types/props/propTypes";
import type { AwningSettings } from "./awningSettings";
import type { CaseworkCatalogSettings } from "./caseworkCatalogSettings";
import type { CaseworkSettings } from "./caseworkSettings";
import type { ColumnCatalogSettings } from "./columnCatalogSettings";
import type { DoorCatalogSettings } from "./doorCatalogSettings";
import type { DoorSettings } from "./doorSettings";
import type { DoorWallFramingSettings } from "./doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "./doorWallWrappingSettings";
import type { DormerSettings } from "./dormerSettings";
import type { DrawingCatalogSettings } from "./drawingCatalogSettings";
import type { ElectricalItemCatalogSettings } from "./electricalItemCatalogSettings";
import type { ElectricalSettings } from "./electricalSettings";
import type { ExteriorDoorCasingSettings } from "./exteriorDoorCasingSettings";
import type { ExteriorMaterialSettings } from "./exteriorMaterialSettings";
import type { ExteriorPropSettings } from "./exteriorPropSettings";
import type { ExteriorTrimSettings } from "./exteriorTrimSettings";
import type { ExteriorWindowCasingSettings } from "./exteriorWindowCasingSettings";
import type { FloorSettings } from "./floorSettings";
import type { FoundationSettings } from "./foundationSettings";
import type { GarageSettings } from "./garageSettings";
import type { HeaderFramingSettings } from "./headerFramingSettings";
import type { InteriorDoorCasingSettings } from "./interiorDoorCasingSettings";
import type { InteriorMaterialSettings } from "./interiorMaterialSettings";
import type { InteriorPropSettings } from "./interiorPropSettings";
import type { InteriorTrimSettings } from "./interiorTrimSettings";
import type { InteriorWindowCasingSettings } from "./interiorWindowCasingSettings";
import type { KeyMeasuresSettings } from "./keyMeasuresSettings";
import type { MasterPlanSettings } from "./masterPlanSettings";
import type { PlanSettings } from "./planSettings";
import type { PorchSettings } from "./porchSettings";
import type { ProfileCatalogSettings } from "./profileCatalogSettings";
import type { RailingCatalogSettings } from "./railingCatalogSettings";
import type { RearPorchSettings } from "./rearPorchSettings";
import type { RoofFramingSettings } from "./roofFramingSettings";
import type { RoofSettings } from "./roofSettings";
import type { ScheduleSettings } from "./scheduleSettings";
import { SettingsType } from "./settingsType";
import type { StairCatalogSettings } from "./stairCatalogSettings";
import type { StructureSettings } from "./structureSettings";
import type { ViewNoteSettings } from "./viewNoteSettings";
import type { WainscotingSettings } from "./wainscotingSettings";
import type { WallCatalogSettings } from "./wallCatalogSettings";
import type { WallFramingSettings } from "./wallFramingSettings";
import type { WallSettings } from "./wallSettings";
import type { WindowCatalogSettings } from "./windowCatalogSettings";
import type { WindowMaterialSettings } from "./windowMaterialSettings";
import type { WindowMullionSettings } from "./windowMullionSettings";
import type { WindowSettings } from "./windowSettings";
import type { WindowShutterSettings } from "./windowShutterSettings";
import type { WindowWallFramingSettings } from "./windowWallFramingSettings";
import type { WindowWallWrappingSettings } from "./windowWallWrappingSettings";

/*
  HomeSettings is a resolved, complete set of Settings for home generation
 */

export type HomeSettings = Readonly<HomeSettingsRW>;

export type PartialHomeSettings = {
  [K in keyof HomeSettings]?: Partial<HomeSettings[K]>;
};

export interface HomeSettingsRW {
  type: "HOME_SETTINGS";
  name?: string; // used by the builtin settings library
  interiorMaterials: InteriorMaterialSettings;
  exteriorMaterials: ExteriorMaterialSettings;
  exteriorProps: ExteriorPropSettings;
  interiorProps: InteriorPropSettings;
  props: PropAttributeTable;
  door: DoorSettings;
  window: WindowSettings;
  roof: RoofSettings;
  dormer: DormerSettings;
  roofFraming: RoofFramingSettings;
  windowFraming: WindowWallFramingSettings;
  windowWrapping: WindowWallWrappingSettings;
  doorFraming: DoorWallFramingSettings;
  doorWrapping: DoorWallWrappingSettings;
  wall: WallSettings;
  wallCatalog: WallCatalogSettings;
  wainscoting: WainscotingSettings;
  wallFraming: WallFramingSettings;
  headerFraming: HeaderFramingSettings;
  awning: AwningSettings;
  porch: PorchSettings;
  planset: PlanSettings;
  masterPlanset: MasterPlanSettings;
  schedule: ScheduleSettings;
  keyMeasures: KeyMeasuresSettings;
  rearPorch?: RearPorchSettings;
  garage: GarageSettings;
  foundation: FoundationSettings;
  exteriorTrim: ExteriorTrimSettings;
  interiorTrim: InteriorTrimSettings;
  structure: StructureSettings;
  electrical: ElectricalSettings;
  windowShutters: WindowShutterSettings;
  windowMullions: WindowMullionSettings;
  windowMaterial: WindowMaterialSettings;
  viewNote: ViewNoteSettings;
  exteriorWindowCasing: ExteriorWindowCasingSettings;
  exteriorDoorCasing: ExteriorDoorCasingSettings;
  interiorDoorCasing: InteriorDoorCasingSettings;
  profile: ProfileCatalogSettings;
  column: ColumnCatalogSettings;
  drawingCatalog: DrawingCatalogSettings;
  caseworkCatalog: CaseworkCatalogSettings;
  casework: CaseworkSettings;
  stair: StairCatalogSettings;
  railing: RailingCatalogSettings;
  doorCatalog: DoorCatalogSettings;
  windowCatalog: WindowCatalogSettings;
  electricalItemCatalog: ElectricalItemCatalogSettings;
  floor: FloorSettings;
  interiorWindowCasing: InteriorWindowCasingSettings;
}

export const homeSettingsFieldToSettingsType: {
  [fieldName in keyof Omit<HomeSettings, "type" | "name">]?: SettingsType;
} = {
  interiorMaterials: SettingsType.INTERIOR_MATERIAL_SETTINGS,
  exteriorMaterials: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
  exteriorProps: SettingsType.EXTERIOR_PROP_SETTINGS,
  interiorProps: SettingsType.INTERIOR_PROP_SETTINGS,
  windowMullions: SettingsType.WINDOW_MULLION_SETTINGS,
  exteriorWindowCasing: SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS,
  windowShutters: SettingsType.WINDOW_SHUTTER_SETTINGS,
  roof: SettingsType.ROOF_SETTINGS,
  dormer: SettingsType.DORMER_SETTINGS,
  roofFraming: SettingsType.ROOF_FRAMING_SETTINGS,
  windowFraming: SettingsType.WINDOW_FRAMING_SETTINGS,
  windowWrapping: SettingsType.WINDOW_WRAPPING_SETTINGS,
  doorFraming: SettingsType.DOOR_FRAMING_SETTINGS,
  doorWrapping: SettingsType.DOOR_WRAPPING_SETTINGS,
  wainscoting: SettingsType.WAINSCOTING_SETTINGS,
  wallFraming: SettingsType.WALL_FRAMING_SETTINGS,
  headerFraming: SettingsType.HEADER_FRAMING_SETTINGS,
  awning: SettingsType.AWNING_SETTINGS,
  wall: SettingsType.WALL_SETTINGS,
  wallCatalog: SettingsType.WALL_CATALOG_SETTINGS,
  door: SettingsType.DOOR_SETTINGS,
  window: SettingsType.WINDOW_SETTINGS,
  windowMaterial: SettingsType.WINDOW_MATERIAL_SETTINGS,
  porch: SettingsType.PORCH_SETTINGS,
  rearPorch: SettingsType.REAR_PORCH_SETTINGS,
  planset: SettingsType.PLAN_SETTINGS,
  masterPlanset: SettingsType.MASTER_PLAN_SETTINGS,
  schedule: SettingsType.SCHEDULE_SETTINGS,
  keyMeasures: SettingsType.KEY_MEASURES_SETTINGS,
  foundation: SettingsType.FOUNDATION_SETTINGS,
  exteriorDoorCasing: SettingsType.EXTERIOR_DOOR_CASING_SETTINGS,
  interiorDoorCasing: SettingsType.INTERIOR_DOOR_CASING_SETTINGS,
  garage: SettingsType.GARAGE_SETTINGS,
  exteriorTrim: SettingsType.EXTERIOR_TRIM_SETTINGS,
  interiorTrim: SettingsType.INTERIOR_TRIM_SETTINGS,
  structure: SettingsType.STRUCTURE_SETTINGS,
  electrical: SettingsType.ELECTRICAL_SETTINGS,
  viewNote: SettingsType.VIEW_NOTE_SETTINGS,
  profile: SettingsType.PROFILE_CATALOG_SETTINGS,
  column: SettingsType.COLUMN_CATALOG_SETTINGS,
  drawingCatalog: SettingsType.DRAWING_CATALOG_SETTINGS,
  caseworkCatalog: SettingsType.CASEWORK_CATALOG_SETTINGS,
  casework: SettingsType.CASEWORK_SETTINGS,
  stair: SettingsType.STAIR_CATALOG_SETTINGS,
  railing: SettingsType.RAILING_CATALOG_SETTINGS,
  doorCatalog: SettingsType.DOOR_CATALOG_SETTINGS,
  windowCatalog: SettingsType.WINDOW_CATALOG_SETTINGS,
  electricalItemCatalog: SettingsType.ELECTRICAL_ITEM_CATALOG_SETTINGS,
  floor: SettingsType.FLOOR_SETTINGS,
  interiorWindowCasing: SettingsType.INTERIOR_WINDOW_CASING_SETTINGS,
};

export const settingsTypeToHomeSettingsField: {
  [key in SettingsType]?: keyof Omit<HomeSettings, "type" | "name">;
} = {};

for (const field in homeSettingsFieldToSettingsType) {
  settingsTypeToHomeSettingsField[homeSettingsFieldToSettingsType[field]] = field;
}
